import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const API_BASE = process.env.REACT_APP_API_BASE;

const getCurrencySymbol = (code) => {
  const symbols = {
    USD: '$',
    INR: '₹',
    AED: 'د.إ',
    EUR: '€',
    GBP: '£',
    SAR: '﷼'
  };
  return symbols[code?.toUpperCase()] || code;
};

export default function Payment() {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [couponValidationMessage, setCouponValidationMessage] = useState('');
  const [isCouponValid, setIsCouponValid] = useState(false);
  const [discountDetails, setDiscountDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, [billingCycle]);

  useEffect(() => {
    document.title = "Payment – Ennoverse";
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/user-country-plans?cycle=${billingCycle}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      setPlans(data);
    } catch (err) {
      console.error('Error fetching plans', err);
      setPlans([]);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    setMessage('');
  };

  function AppsPreview({ apps }) {
    const [expanded, setExpanded] = useState(false);
    return (
      <>
        <span
          className="cursor-pointer text-blue-600"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {apps.length} ▼
        </span>
        {expanded && (
          <div className="mt-2 text-xs bg-gray-100 p-2 rounded shadow">
            <ul className="list-disc ml-4">
              {apps.map((app, i) => (
                <li key={i}>{app}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  const handleCheckout = async () => {
    if (!selectedPlanId) {
      setMessage('Please select a plan to continue.');
      return;
    }

    const selectedPlan = plans.find(p => p._id === selectedPlanId);
    if (!selectedPlan) {
      setMessage('Invalid plan selected.');
      return;
    }

    try {
      setLoading(true);

      if (selectedPlan.price > 0) {
        const res = await fetch(`${API_BASE}/api/payment/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ 
            planId: selectedPlanId, 
            coupon: couponCode,
            billingCycle,
            promotionId: discountDetails?.promotionId || null
          }),
        });

        const data = await res.json();
        if (res.ok && data.url) {
          const stripe = await stripePromise;
          window.location.href = data.url;
        } else {
          setMessage(data.message || 'Payment session failed.');
        }
      } else {
        const assignRes = await fetch(`${API_BASE}/api/users/select-plan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ planId: selectedPlanId }),
        });

        if (assignRes.ok) {
          window.location.href = '/complete-profile';
        } else {
          const errData = await assignRes.json();
          setMessage(errData.message || 'Failed to assign free plan.');
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setMessage('An error occurred during checkout.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <img src="/ennoverse.png" alt="Ennoverse Logo" className="h-10" />
          <button
            onClick={() => navigate('/selectindustry')}
            className="inline-flex items-center text-sm text-blue-600 hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Change Industry
          </button>
        </div>

        <h2 className="text-2xl font-semibold text-center mb-6">Choose Your Plan</h2>

        {message && <div className="mb-4 text-red-600 text-center">{message}</div>}

        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded border ${billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-black'}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annually')}
            className={`px-4 py-2 rounded border ${billingCycle === 'annually' ? 'bg-blue-600 text-white' : 'bg-white text-black'}`}
          >
            Yearly (Save more)
          </button>
        </div>

        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
          {plans.map((plan) => (
            <div
              key={plan._id}
              onClick={() => handlePlanSelect(plan._id)}
              className={`cursor-pointer border rounded-lg p-4 shadow-md hover:shadow-lg transition ${
                selectedPlanId === plan._id ? 'border-blue-600 ring-2 ring-blue-500' : 'border-gray-300'
              }`}
            >
              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <p className="text-gray-600 mb-3">{plan.description}</p>
              <div className="text-sm text-gray-700 space-y-1">
                <div><strong>Requests/Month:</strong> {plan.requestLimit ?? 'Unlimited'}</div>
                <div className="flex items-center gap-1">
                  <strong>Apps:</strong>
                  {plan.apps === 'all' ? (
                    <span>All</span>
                  ) : (
                    <AppsPreview apps={plan.apps} />
                  )}
                </div>
                <div><strong>Priority Support:</strong> {plan.prioritySupport ? 'Yes' : 'No'}</div>
                <div><strong>Users:</strong> {plan.users ?? 1}</div>
              </div>
              <p className="text-lg font-semibold mt-3">
                {selectedPlanId === plan._id && isCouponValid && discountDetails ? (
                  <>
                    <span className="line-through text-sm text-gray-500 mr-1">
                      {billingCycle === 'annually'
                        ? `${getCurrencySymbol(plan.currency)}${plan.yearlyPrice.toLocaleString()}`
                        : `${getCurrencySymbol(plan.currency)}${plan.monthlyPrice.toLocaleString()}`
                      }
                    </span>
                    <span className="text-green-600 font-bold">
                      {getCurrencySymbol(plan.currency)}{discountDetails.finalAmount.toLocaleString()} {billingCycle === 'annually' ? '/ Year' : '/ Month'}
                    </span>
                  </>
                ) : (
                  billingCycle === 'annually' ? (
                    plan.yearlyPrice > 0 ? (
                      <>
                        <span className="line-through text-sm text-gray-500 mr-1">
                          {getCurrencySymbol(plan.currency)}{(plan.monthlyPrice * 12).toLocaleString()}
                        </span>
                        {getCurrencySymbol(plan.currency)}{plan.yearlyPrice.toLocaleString()} / Year
                      </>
                    ) : 'Free'
                  ) : (
                    plan.monthlyPrice > 0
                      ? `${getCurrencySymbol(plan.currency)}${plan.monthlyPrice.toLocaleString()} / Month`
                      : 'Free'
                  )
                )}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-4">
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="bg-primary hover:bg-red-600 text-white px-6 py-2 rounded"
          >
            {loading ? 'Processing...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
